import { useCallback, useRef, useState } from "react";

/**
 * `useGeolocation` — the ONE place the app acquires PRECISE device coordinates
 * (facility-discovery B1, ADR-024 §"area-first, opt-in").
 *
 * Its contract is privacy-shaped: it NEVER auto-fires on mount — precise
 * geolocation is collected only on an explicit `request()` (the "Use my
 * location" tap), so mounting the hook never pops the browser permission
 * prompt. The status machine:
 *
 *   idle → (request()) → prompting → one of
 *     · granted     { latitude, longitude }   (permission allow + a fix)
 *     · denied                                (PERMISSION_DENIED)
 *     · unavailable                           (no navigator.geolocation at all,
 *                                              or a non-denial error: timeout /
 *                                              POSITION_UNAVAILABLE)
 *
 * A denial / unavailable NEVER leaves fabricated coordinates — only a `granted`
 * fix exposes lat/lng.
 */
export type GeolocationStatus =
  | "idle"
  | "prompting"
  | "granted"
  | "denied"
  | "unavailable";

export interface UseGeolocation {
  status: GeolocationStatus;
  latitude?: number;
  longitude?: number;
  /** Explicitly acquire a one-shot precise fix (the "Use my location" trigger). */
  request: () => void;
}

// W3C GeolocationPositionError.code — only PERMISSION_DENIED is a true "denied";
// every other error (POSITION_UNAVAILABLE, TIMEOUT) is recoverable → unavailable.
const PERMISSION_DENIED = 1;

export function useGeolocation(): UseGeolocation {
  const [status, setStatus] = useState<GeolocationStatus>("idle");
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);

  // Guard against a stale fix landing after the component re-requested / changed
  // state: only the most-recent request may write coordinates.
  const requestSeq = useRef(0);

  const request = useCallback(() => {
    const seq = ++requestSeq.current;

    const geolocation =
      typeof navigator !== "undefined" ? navigator.geolocation : undefined;

    // Detect a missing Geolocation API up front — do NOT call into it.
    if (!geolocation || typeof geolocation.getCurrentPosition !== "function") {
      setStatus("unavailable");
      setLatitude(undefined);
      setLongitude(undefined);
      return;
    }

    setStatus("prompting");
    setLatitude(undefined);
    setLongitude(undefined);

    geolocation.getCurrentPosition(
      (pos) => {
        if (seq !== requestSeq.current) return;
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
        setStatus("granted");
      },
      (err) => {
        if (seq !== requestSeq.current) return;
        // A denial is distinct from a transient failure: only PERMISSION_DENIED
        // is "denied"; timeout / position-unavailable are "unavailable".
        setStatus(err?.code === PERMISSION_DENIED ? "denied" : "unavailable");
        setLatitude(undefined);
        setLongitude(undefined);
      },
    );
  }, []);

  return { status, latitude, longitude, request };
}
